'use client';

import React from 'react';

export interface ToolProgressShimmerProps {
  /** Short label, e.g. "Working…" or "Running: find_contact…" */
  label: string;
  /** Optional class for the container */
  className?: string;
}

const toolProgressShimmerStyles = `
  @keyframes tool-progress-shimmer {
    0% { transform: translateX(-100%); opacity: 0.4; }
    50% { opacity: 1; }
    100% { transform: translateX(100%); opacity: 0.4; }
  }
  .tool-progress-shimmer-bar::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
    animation: tool-progress-shimmer 1.5s ease-in-out infinite;
  }
  .dark .tool-progress-shimmer-bar::after {
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
  }
`;

/**
 * Shimmer + text progress indicator for tool execution.
 * Shown while the model is executing tools (e.g. find_contact, search_crm).
 */
export function ToolProgressShimmer({ label, className = '' }: ToolProgressShimmerProps) {
  return (
    <>
      <style>{toolProgressShimmerStyles}</style>
      <div
        className={`flex items-center gap-3 px-4 py-2 rounded-lg border border-border bg-card text-gray-800 dark:text-gray-200 ${className}`}
        role="status"
        aria-live="polite"
        aria-label={label}
      >
        <div className="flex gap-1.5 flex-shrink-0">
          <div className="tool-progress-shimmer-bar relative h-2 w-8 rounded-full bg-muted overflow-hidden" />
          <div className="tool-progress-shimmer-bar relative h-2 w-6 rounded-full bg-muted overflow-hidden" />
          <div className="tool-progress-shimmer-bar relative h-2 w-5 rounded-full bg-muted overflow-hidden" />
        </div>
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{label}</span>
      </div>
    </>
  );
}
